'use client';
import { LanguageLinksContext } from '@mnd-apps/public/contexts/LanguageLinksContext';
import { track } from '@mnd-apps/public/lib/tracker';
import { useClickOutside, useWindowScrollHandler } from '@mnd-frontend/hooks';
import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import Button from '@mnd-frontend/ui/public/components/Button';
import NavLanguage from '@mnd-frontend/ui/public/components/Navigation/NavLanguage';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type { CurrentLocaleDataType } from '@mnd-frontend/ui/public/types/locale';
import type { ExtraNavItemType, PrimaryNavItemType } from '@mnd-frontend/ui/public/types/menu';
import { formatCardMenuItem, trackMenuClick } from '@mnd-frontend/ui/public/utils/formatters/menu';
import { CUBIC_BEZIER } from '@mnd-frontend/ui/shared/constants/animations';
import { KEYS } from '@mnd-frontend/ui/shared/constants/keypress';
import {
  BREAKPOINTS,
  SIZING_2,
  SIZING_3,
  SIZING_5,
  SPACING_0_5,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
  SPACING_6,
  SPACING_8,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment, type KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const NavItemCard = dynamic(
  () => import('@mnd-frontend/ui/public/components/Navigation/NavItemCard'),
);

// #region Styled components
const Navigation = styled.nav`
// Hide it during hydration.
display: none;

@media (min-width: ${BREAKPOINTS.XL}px) {
 display: flex;
 justify-content: space-between;
 width: 100%;
}
`;

const NavPrimary = styled.menu`
  display: flex;
  gap: ${SIZING_3};
  list-style: none;
  padding: 0;
  margin: 0;

  .item-primary {
    display: flex;
    align-items: center;
  }
`;

const NavExtra = styled.menu`
  display: flex;
  align-items: center;
  gap: ${SPACING_1_5};
  list-style: none;
  padding: 0;
  margin: 0;

  .item {
    display: flex;
    align-items: center;
  }
`;

const NavItemDropdownButton = styled.button<{ $isOpen: boolean }>`
  font-size: ${SIZING_2};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${SPACING_0_5};
  text-align: left;
  min-height: ${SIZING_5};
  width: fit-content;
  padding: 0;
  line-height: 120%;
  cursor: pointer;
  color: inherit;
  background-color: transparent;
  transition: color 500ms ease;

  .mnd-icon {
    transition: transform 300ms ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  }

  &:hover {
    color: ${WEB_RED_REG};
  }
`;

const NavLink = styled(Link)`
  font-size: ${SIZING_2};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${SPACING_3};
  text-align: left;
  min-height: ${SIZING_5};
  width: fit-content;
  padding: 0;
  line-height: 120%;

  &:hover {
    color: ${WEB_RED_REG};
  }
`;

const Dropdown = styled.div<{ $open: boolean }>`
    background-color: white;
    position: fixed;
    top: var(--header-height);
    left: 0;
    right: 0;
    border-bottom-left-radius: ${SPACING_6};
    border-bottom-right-radius: ${SPACING_6};
    padding: ${SPACING_2} 0 ${SPACING_8} 0;
    max-height: calc(100vh - 6.5rem);
    overflow: auto;
    overscroll-behavior: contain;
    clip-path: inset(0px 0px 100% round 0px 0px ${SPACING_3} ${SPACING_3});
    transition: clip-path 300ms ${CUBIC_BEZIER}, visibility 0.5s linear;
    
    ${({ $open }) =>
      $open &&
      css`
      visibility: visible;
      clip-path: inset(0px 0px 0% round 0px 0px ${SPACING_3} ${SPACING_3});
  `}
`;

const NavContentWrapper = styled.ul`
  display: grid;
  gap: ${SPACING_2};
  grid-template-columns: repeat(3, 1fr);
  grid-auto-flow: row;
  list-style: none;
  margin: 0;
  padding: 0;
`;
// #endregion

const IS_MOBILE = false;

const toggleBlurViaDom = (setBlur: boolean) => {
  if (typeof window !== 'undefined') {
    const bodyElement = window.document.querySelector('body');

    if (!bodyElement) return;

    bodyElement.style.setProperty('--body-blur-opacity', `${setBlur ? 1 : 0}`);
  }
};

// #region Start Component
const HeaderNavDesktop = ({
  primaryNav,
  extraNav,
  currentLocale,
}: {
  primaryNav: Array<PrimaryNavItemType> | undefined;
  extraNav: Array<ExtraNavItemType> | undefined;
} & CurrentLocaleDataType) => {
  // #endregion
  const pathname = usePathname();
  const history = useRef(pathname);
  const localePathsMap = useContext(LanguageLinksContext);
  const { t } = useTranslation();

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeContentIndex, setActiveContentIndex] = useState<string | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window && buttonRef.current) {
      const bodyElement = window.document.querySelector('body');
      const windowWidth = window.innerWidth;
      const position = buttonRef.current.getClientRects().item(0);
      const rtlPositionX = windowWidth - (position?.right ?? 0);

      if (bodyElement) {
        bodyElement.style.setProperty('--i18n-x-distance', rtlPositionX + 'px');
      }
    }
  }, []);

  useEffect(() => {
    if (history.current !== pathname) {
      setIsNavOpen(false);
      setActiveContentIndex(null);
      toggleBlurViaDom(false);

      history.current = pathname;
    }
  }, [pathname]);

  const closeNav = () => {
    setIsNavOpen(false);
    setActiveContentIndex(null);
    toggleBlurViaDom(false);
  };

  const handleOnKeyUp = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === KEYS.ESCAPE) {
      closeNav();
    }
  };

  const navRef = useRef(null);
  useClickOutside(navRef, () => {
    closeNav();
  });

  useWindowScrollHandler(closeNav);

  const handleNavToggle = (label: string) => {
    if (activeContentIndex === label) {
      closeNav();
    } else {
      setIsNavOpen(true);
      setActiveContentIndex(label);
      toggleBlurViaDom(true);
    }

    return false;
  };

  const isActive = (label: string | null) =>
    label !== null && isNavOpen && activeContentIndex === label;

  // #region JSX Render
  return (
    <Navigation onKeyUp={handleOnKeyUp} aria-label={t('aria.label.primary')} ref={navRef}>
      <NavPrimary role="menubar">
        {(primaryNav ?? []).map(item => (
          <li key={item.id} className="item-primary">
            {!item.childItems?.nodes?.length ? (
              <NavLink
                href={item.path ?? ''}
                role="menuitem"
                onClick={() =>
                  trackMenuClick(() => track, {
                    category: item.label,
                    label: item.label ?? '',
                    isMobile: IS_MOBILE,
                  })
                }
              >
                {item.label}
              </NavLink>
            ) : (
              <Fragment>
                <NavItemDropdownButton
                  type="button"
                  onClick={() => handleNavToggle(item.label ?? '')}
                  role="menuitem"
                  aria-expanded={isActive(item.label)}
                  aria-controls={item.id}
                  $isOpen={isActive(item.label)}
                >
                  {item.label}
                  <IconChevronDown aria-hidden />
                </NavItemDropdownButton>
                <Dropdown $open={isActive(item.label)}>
                  <ContentMaxWidth>
                    <NavContentWrapper as="ul" id={item.id}>
                      {(item.childItems?.nodes ?? []).map((secItem, i) => (
                        <NavItemCard
                          as="li"
                          key={secItem?.id ?? `sub-menu-${i}`}
                          heading={secItem?.label}
                          description={secItem?.description}
                          href={secItem?.path ?? ''}
                          currentPath={pathname}
                          mainNavOnClick={() =>
                            trackMenuClick(() => track, {
                              category: item.label,
                              label: secItem?.label,
                              isMobile: IS_MOBILE,
                            })
                          }
                          subMenuList={{
                            items: formatCardMenuItem(secItem?.childItems?.nodes, {
                              tracker: () => track,
                              category: item.label,
                              isMobile: IS_MOBILE,
                            }),
                          }}
                          disableKeybordNavigation={!isNavOpen || item.label !== activeContentIndex}
                        />
                      ))}
                    </NavContentWrapper>
                  </ContentMaxWidth>
                </Dropdown>
              </Fragment>
            )}
          </li>
        ))}
      </NavPrimary>
      <NavExtra role="menubar">
        <li className="item">
          <NavLanguage
            currentLocale={currentLocale}
            localePathsMap={localePathsMap}
            hide={false}
            buttonRef={buttonRef}
          />
        </li>
        {extraNav?.map(item => (
          <li key={item.id} className="item">
            {!item.css ? (
              <NavLink
                onClick={() =>
                  trackMenuClick(() => track, {
                    category: 'signup-login',
                    label: item.label ?? '',
                    isMobile: IS_MOBILE,
                  })
                }
                href={item.path}
              >
                {item.label}
              </NavLink>
            ) : (
              <Button
                onClick={() =>
                  trackMenuClick(() => track, {
                    category: 'signup-login',
                    label: item.label ?? '',
                    isMobile: IS_MOBILE,
                  })
                }
                configuration={item.css.includes('ghost') ? 'ghost' : 'primary'}
                href={item.path}
              >
                {item.label}
              </Button>
            )}
          </li>
        ))}
      </NavExtra>
    </Navigation>
  );
  // #endregion
};

export default HeaderNavDesktop;
